import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Syringe, MapPin, UserCog, FileText, CreditCard, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import TreatmentModal from "@/components/patients/TreatmentModal";
import FollowUpBadge from "@/components/followups/FollowUpBadge";
import FollowUpActionButtons from "@/components/followups/FollowUpActionButtons";
import FollowUpActionModal from "@/components/followups/FollowUpActionModal";
import { PATIENTS, FOLLOWUPS, TREATMENTS } from "@/constants/testIds";

const PATIENT_FOLLOWUP_TEST_IDS = {
  contact: FOLLOWUPS.patientContactButtonPrefix,
  postpone: FOLLOWUPS.patientPostponeButtonPrefix,
  discard: FOLLOWUPS.patientDiscardButtonPrefix,
  reactivate: FOLLOWUPS.patientReactivateButtonPrefix,
};

const PAYMENT_METHOD_LABELS = { cash: "Efectivo", card: "Tarjeta", transfer: "Transferencia", other: "Otro" };
const REVIEW_STATUS_LABELS = { active: "Revisión vigente", expired: "Revisión vencida", used: "Revisión utilizada" };
const REVIEW_STATUS_STYLES = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  expired: "bg-amber-50 text-amber-700 border-amber-200",
  used: "bg-muted text-muted-foreground border-border",
};

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" }) +
    " \u00b7 " + d.toTimeString().slice(0, 5);
}

function zonesSummary(zones) {
  if (!zones?.length) return null;
  return zones.map((z) => (z.units != null ? `${z.zone_name} (${z.units})` : z.zone_name)).join(" \u00b7 ");
}

/** Historial cronologico de tratamientos aplicados al paciente. Cada
 * tratamiento con seguimiento activo (next_due_date) muestra su estado de
 * recurrencia (vencida/por vencer/en riesgo) y permite actuar sobre el -
 * Admin/Recepcion pueden contactar/posponer/descartar/reactivar, Profesional
 * solo visualiza (RBAC del backend, /api/followups). */
export default function TreatmentTimeline({ patientId, patientName, canRegister, professionals, services, products, currentUser }) {
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [followUpModal, setFollowUpModal] = useState({ mode: null, treatment: null });
  const { toast } = useToast();
  const navigate = useNavigate();
  const canActFollowUp = currentUser?.role === "admin" || currentUser?.role === "reception";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/patients/${patientId}/treatments`);
      setTreatments(data);
    } catch (err) {
      toast({ title: "Error al cargar tratamientos", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [patientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreated = (created) => {
    toast({ title: "Tratamiento registrado" });
    if (created?.low_stock_warnings?.length > 0) {
      toast({
        title: "Atención: stock bajo",
        description: created.low_stock_warnings.join(" \u00b7 "),
        variant: "destructive",
      });
    }
    load();
  };

  const handleReactivate = async (treatment) => {
    try {
      await api.post(`/followups/${treatment.id}/reactivate`);
      toast({ title: "Seguimiento reactivado" });
      load();
    } catch (err) {
      toast({ title: "Error al reactivar", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex items-center justify-between">
        <h3 className="text-xs uppercase tracking-[0.1em] text-muted-foreground font-medium">Historial de tratamientos</h3>
        {canRegister && (
          <Button size="sm" className="bg-brand hover:bg-brand-hover text-white" onClick={() => setModalOpen(true)} data-testid={PATIENTS.treatmentAddButton}>
            <Plus className="w-3.5 h-3.5" /> Registrar tratamiento
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-muted-foreground text-sm">Cargando...</p>
      ) : treatments.length === 0 ? (
        <div className="bg-white border border-border rounded-xl p-8 text-center" data-testid={PATIENTS.treatmentEmptyState}>
          <Syringe className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Todavía no hay tratamientos registrados.</p>
        </div>
      ) : (
        <div className="relative border-l border-border pl-6 space-y-6">
          {treatments.map((t) => (
            <div key={t.id} className="relative" data-testid={`${PATIENTS.treatmentItemPrefix}${t.id}`}>
              <span className="absolute -left-[29px] top-1 w-2.5 h-2.5 rounded-full bg-brand" />
              <p className="text-sm text-foreground font-medium capitalize">{formatDate(t.treatment_date)}</p>
              <div className="bg-white border border-border rounded-xl p-4 mt-1.5 space-y-1.5 text-sm">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="font-medium text-foreground">{t.service_name || t.product_notes || "Tratamiento"}</p>
                  <Button
                    variant="ghost" size="sm" className="h-7 px-2 text-xs"
                    onClick={() => navigate(`/pacientes/${patientId}/tratamientos/${t.id}`)}
                    data-testid={`${TREATMENTS.itemViewDetailButtonPrefix}${t.id}`}
                  >
                    <FileText className="w-3.5 h-3.5" /> Ver ficha completa
                  </Button>
                </div>
                <div className="flex items-center gap-1.5 text-muted-foreground text-xs">
                  <UserCog className="w-3.5 h-3.5" /> {t.professional_name}
                </div>
                {zonesSummary(t.zones) && (
                  <div className="flex items-center gap-1.5 text-muted-foreground text-xs" data-testid={`${TREATMENTS.itemUnitsSummary}${t.id}`}>
                    <MapPin className="w-3.5 h-3.5" /> {zonesSummary(t.zones)}
                    {t.total_units ? <span className="font-medium text-foreground"> \u00b7 Total: {t.total_units} {t.primary_product_unit || ""}</span> : null}
                  </div>
                )}
                {t.products_used?.length > 0 && (
                  <p className="text-muted-foreground text-xs">
                    Insumos: {t.products_used.map((p) => `${p.product_name} (${p.quantity} ${p.unit})`).join(", ")}
                  </p>
                )}
                {t.observations && <p className="text-muted-foreground text-xs pt-1">{t.observations}</p>}

                <div className="flex items-center gap-2 flex-wrap pt-1">
                  <span
                    className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border bg-muted text-muted-foreground"
                    data-testid={`${TREATMENTS.itemPaymentBadgePrefix}${t.id}`}
                  >
                    <CreditCard className="w-3 h-3" />
                    {t.payment_info ? `Pagado: $${t.payment_info.amount} (${PAYMENT_METHOD_LABELS[t.payment_info.method] || t.payment_info.method})` : "Sin pago registrado"}
                  </span>
                  {t.review_status && (
                    <Badge className={`${REVIEW_STATUS_STYLES[t.review_status]} border font-normal text-[11px]`} data-testid={`${TREATMENTS.itemReviewBadgePrefix}${t.id}`}>
                      <CalendarClock className="w-3 h-3 mr-1" /> {REVIEW_STATUS_LABELS[t.review_status]}
                    </Badge>
                  )}
                  {t.touchup_count > 0 && (
                    <span className="text-[11px] px-2 py-0.5 rounded-full border border-border text-muted-foreground" data-testid={`${TREATMENTS.itemTouchupCountPrefix}${t.id}`}>
                      {t.touchup_count} retoque{t.touchup_count > 1 ? "s" : ""}
                    </span>
                  )}
                </div>

                {t.urgency && (
                  <div className="pt-2 border-t border-border mt-2 flex items-center justify-between flex-wrap gap-2" data-testid={`${FOLLOWUPS.patientBadgePrefix}${t.id}`}>
                    <FollowUpBadge bucket={t.urgency.bucket} daysOverdue={t.urgency.days_overdue} followUpStatus={t.follow_up_status} />
                    {canActFollowUp && (
                      <FollowUpActionButtons
                        treatment={{ id: t.id, patient_name: patientName, service_name: t.service_name, follow_up_status: t.follow_up_status }}
                        onContact={(tr) => setFollowUpModal({ mode: "contact", treatment: tr })}
                        onPostpone={(tr) => setFollowUpModal({ mode: "postpone", treatment: tr })}
                        onDiscard={(tr) => setFollowUpModal({ mode: "discard", treatment: tr })}
                        onReactivate={handleReactivate}
                        testIds={PATIENT_FOLLOWUP_TEST_IDS}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <TreatmentModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        patientId={patientId}
        professionals={professionals}
        services={services}
        products={products}
        currentUser={currentUser}
        onCreated={handleCreated}
      />

      <FollowUpActionModal
        open={Boolean(followUpModal.mode)}
        onOpenChange={(open) => !open && setFollowUpModal({ mode: null, treatment: null })}
        mode={followUpModal.mode}
        treatment={followUpModal.treatment}
        onSuccess={() => { toast({ title: "Seguimiento actualizado" }); load(); }}
      />
    </div>
  );
}
